import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { LeadForm } from "@/components/common/LeadForm";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";

export default function Contact() {
  const { t } = useLang();

  return (
    <div data-testid="contact-page">
      <SEO
        title={`Contact Orion FM — Get a Free Quote | ${COMPANY.brand}`}
        description="Contact Orion Facilities Management for a free site inspection. Reply within 1 business hour. Dubai, Abu Dhabi, Sharjah."
      />

      <section className="bg-white py-20 lg:py-28">
        <div className="container-orion">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="text-eyebrow">{t("contact.sectionLabel")}</div>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium leading-[1.02] text-slate-900">
                {t("contact.title")}
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">{t("contact.desc")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 lg:p-10 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource="contact" />
            </div>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-slate-950 text-white rounded-2xl p-8">
              <div className="text-eyebrow text-blue-400">Direct line</div>
              <a
                data-testid="contact-phone-link"
                href={`tel:${COMPANY.phone}`}
                className="mt-4 block font-heading text-2xl lg:text-3xl tracking-tight font-medium hover:text-blue-300 transition-colors"
              >
                {COMPANY.phoneDisplay}
              </a>
              <a
                data-testid="contact-whatsapp-link"
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                {t("common.whatsapp")}
              </a>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-5">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-700 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                    Email
                  </div>
                  <a href={`mailto:${COMPANY.email}`} className="text-slate-900 hover:text-blue-700">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-700 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                    {t("contact.addressLabel")}
                  </div>
                  <div className="text-slate-900">{t("contact.address")}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-700 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                    {t("contact.hoursLabel")}
                  </div>
                  <div className="text-slate-900">{t("contact.hours")}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-blue-700 mt-0.5" />
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                    {t("contact.phoneLabel")}
                  </div>
                  <div className="text-slate-900">
                    <a href={`tel:${COMPANY.phone}`} className="hover:text-blue-700 block">
                      {COMPANY.phoneDisplay}
                      <span className="ms-2 text-xs font-semibold text-green-600">WhatsApp</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Embedded Google Map — keyless iframe (no billing risk) */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="aspect-[4/3]">
                <iframe
                  data-testid="contact-map"
                  title="Orion FM — Ajman Free Zone"
                  src="https://maps.google.com/maps?q=Orion+Facilities+Management+Ajman+Free+Zone&ll=25.41919,55.45273&z=15&output=embed"
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <div className="p-5 border-t border-slate-200 bg-slate-50">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                  {t("contact.addressLabel")}
                </div>
                <div className="mt-1 text-sm text-slate-700 leading-relaxed">
                  {t("contact.address")}
                </div>
                <a
                  data-testid="contact-directions-link"
                  href="https://www.google.com/maps/dir/?api=1&destination=25.41919,55.45273&destination_place_id=ChIJU3HJp4RXXz4Rs7x2839vey8"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  {t("contact.getDirections")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
